import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import type { FileId } from '@freelancer/file-storage';
import { FileStorage, StorageBucket } from '@freelancer/file-storage';
import { isImageFile } from '@freelancer/ui';
import { FileDisplaySize } from '@freelancer/ui/file-display';
import { FileSelectComponent } from '@freelancer/ui/file-select';
import { VerticalAlignment } from '@freelancer/ui/grid';
import { ListItemPadding } from '@freelancer/ui/list-item';
import { ProgressSize } from '@freelancer/ui/progress-bar';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { isDefined, isFormControl } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { Observable, Subscription } from 'rxjs';
import { of, combineLatest, firstValueFrom } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

enum UploadStatus {
  FAILED = 'failed',
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
}
interface UploadedFile {
  readonly file: File;
  readonly previewUrl$: Observable<string | null>;
  readonly status$: Observable<UploadStatus>;
  readonly uploadProgress$: Observable<number>;
  readonly id$: Observable<FileId>;
}

@UntilDestroy({ className: 'FileUploadComponent' })
@Component({
  selector: 'fl-file-upload',
  template: `
    <ng-container *ngIf="fileUploadControl as control">
      <fl-file-select
        #fileSelect
        *ngIf="isFormControl(control)"
        class="FileUpload-uploadArea"
        flTrackingLabel="EnterpriseFileUpload"
        i18n-note="File upload types"
        note="Upload either JPG, JPEG, PNG, GIF, BMP or SVG files (5MB max)"
        [control]="control"
        [flMarginBottom]="'xsmall'"
        [alignment]="'left'"
      >
      </fl-file-select>
      <fl-list
        *ngIf="uploadedFile"
        [bottomBorder]="true"
      >
        <fl-list-item [padding]="ListItemPadding.MID">
          <fl-grid [vAlign]="VerticalAlignment.VERTICAL_CENTER">
            <fl-col
              class="FileUpload-fileName"
              [col]="6"
            >
              <fl-file-display
                *ngIf="uploadedFile.previewUrl$ | flAsync as uploadedFile"
                alt="file preview"
                i18n-alt="File preview alt text"
                [flMarginRight]="'small'"
                [size]="FileDisplaySize.MID"
                [src]="uploadedFile"
              ></fl-file-display>
              <p class="text-xxsmall FileUpload-fileName-text">
                {{ uploadedFile.file.name }}
              </p>
            </fl-col>
            <fl-col [col]="3">
              <p class="text-xxsmall">
                {{ uploadedFile.file.size | fileSize }}
              </p>
            </fl-col>
            <fl-col
              *ngIf="uploadedFile.status$ | flAsync as status"
              [col]="3"
              [flexContainer]="true"
            >
              <ng-container *ngIf="status === UploadStatus.IN_PROGRESS">
                <fl-progress-bar
                  *ngIf="
                    uploadedFile.uploadProgress$ | flAsync as uploadProgress
                  "
                  label="File uploading"
                  i18n-label="file upload progress bar's label"
                  [flMarginRight]="'small'"
                  [progressPercentage]="uploadProgress"
                  [size]="ProgressSize.LARGE"
                ></fl-progress-bar>
              </ng-container>
              <ng-container *ngIf="status === UploadStatus.FAILED">
                <p
                  class="text-error text-xxsmall"
                  i18n="unsuccessful enterprise file upload"
                >
                  Upload Failed. Please try again
                </p>
              </ng-container>
            </fl-col>
          </fl-grid>
        </fl-list-item>
      </fl-list>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent implements OnInit, OnDestroy {
  FileDisplaySize = FileDisplaySize;
  FontColor = FontColor;
  isFormControl = isFormControl;
  ListItemPadding = ListItemPadding;
  ProgressSize = ProgressSize;
  TextSize = TextSize;
  UploadStatus = UploadStatus;
  VerticalAlignment = VerticalAlignment;

  @Input() fileUploadControl: FormControl<readonly File[]>;
  @Input() storageBucket: StorageBucket;
  @Output() onUploadSuccess = new EventEmitter<string>();

  uploadedFile?: UploadedFile;
  private fileUploadSubscription?: Subscription;

  @ViewChild('fileSelect')
  fileSelect: FileSelectComponent;

  constructor(private fileStorage: FileStorage) {}

  ngOnInit(): void {
    this.fileUploadSubscription = this.fileUploadControl.valueChanges
      .pipe(
        filter(isDefined),
        switchMap((files: readonly File[]) => files),
      )
      .subscribe(file => {
        if (this.fileUploadControl.valid) {
          this.uploadFile(file);
        }
      });
  }

  private uploadFile(file: File): void {
    const previewUrl$ = isImageFile(file.name)
      ? this.fileSelect.readFileAsDataURL(file)
      : of(null);
    const uploadTask = this.fileStorage.upload(this.storageBucket, file);
    const uploadProgress$ = uploadTask.percentageChanges();
    const id$ = uploadTask.changes().pipe(map(upload => upload.metadata.id));
    const status$ = combineLatest([uploadTask.changes(), uploadProgress$]).pipe(
      map(([upload, progressRate]) =>
        upload.state === 'error'
          ? UploadStatus.FAILED
          : progressRate === 100
            ? UploadStatus.SUCCESS
            : UploadStatus.IN_PROGRESS,
      ),
    );
    this.uploadedFile = { file, previewUrl$, uploadProgress$, status$, id$ };

    firstValueFrom(
      combineLatest([
        uploadTask.completes(),
        id$.pipe(distinctUntilChanged()),
      ]).pipe(untilDestroyed(this)),
    ).then(([taskEvent, id]) => {
      if (taskEvent.state === 'success' && id !== undefined) {
        this.onUploadSuccess.emit(id);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.fileUploadSubscription) {
      this.fileUploadSubscription.unsubscribe();
    }
  }
}
